/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';
import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {StateDeclarationBuilder, AdnRouterHelperApi} from '../routing/state-utils';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {PrintLineItemApiService, PrintLineItemModel} from './print-line-item-api.service';
import {PrintLineItemEditPageComponent} from './print-line-item-edit-page.component';
import {PrintLineItemListPageComponent} from './print-line-item-list-page.component';


export function printLineItemStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(PrintLineItemApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.manageAdvertising.apiKey)
    && !permissions.hasAny(PERMISSIONS.axAdvertiser.apiKey);
  const rh = adnRouterHelper.createRouterHelper(
    'print-line-items.print-line-item', PrintLineItemApiService, 'app.adv', 'app'
  );
  const titleOptions = {objectTypeKey: 'PrintLineItem', crumbParams: [{type: 'Advertiser'}]};

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: PrintLineItemListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      printLineItemPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<PrintLineItemModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []},
            true, readOnly(), api, 'Print Line Item', 'name', undefined, undefined, titleOptions
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Print Line Item', 'name', undefined, undefined, titleOptions
        ));
      },
      model: function(printLineItemPage: SignalAdminResourcePageState<PrintLineItemModel>) {
        return printLineItemPage.model();
      }
    },
    views: {
      'content@app': {component: PrintLineItemEditPageComponent, bindings: {state: 'printLineItemPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'printLineItemPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'printLineItemPage'}}
    }
  }));
  return states.build();
}
